import React from 'react';
import { Sunrise, Sun, Moon } from 'lucide-react';
import { DashboardCard } from './DashboardLayout';
import { cn } from '../ui/utils';

interface Meal {
  id: string;
  name: string;
  image?: string;
  cookTime?: number;
}

interface TodaysMealsProps {
  meals?: {
    breakfast?: Meal;
    lunch?: Meal;
    dinner?: Meal;
  };
  onMealClick?: (mealId: string) => void;
  className?: string;
}

export function TodaysMeals({ meals, onMealClick, className }: TodaysMealsProps) {
  const mealTypes = [
    { key: 'breakfast', label: 'Breakfast', icon: Sunrise, color: 'text-amber-500' },
    { key: 'lunch', label: 'Lunch', icon: Sun, color: 'text-coral' },
    { key: 'dinner', label: 'Dinner', icon: Moon, color: 'text-sage' },
  ] as const;

  const hasMeals = meals && Object.values(meals).some(meal => meal);

  return (
    <DashboardCard
      className={cn('col-span-1 md:col-span-4 lg:col-span-4', className)}
      span={{ mobile: 1, tablet: 4, desktop: 4 }}
    >
      <h2 className="mb-4 text-xl font-semibold text-dusk">Today's Meals</h2>

      {!hasMeals ? (
        <div className="flex flex-col items-center justify-center py-8 text-center">
          <div className="mb-3 rounded-full bg-neutral-100 p-4">
            <Sun className="h-8 w-8 text-supporting" />
          </div>
          <p className="text-sm text-supporting">
            No meals planned for today
          </p>
          <p className="mt-1 text-xs text-supporting">
            Generate a meal plan to get started
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {mealTypes.map(({ key, label, icon: Icon, color }) => {
            const meal = meals?.[key];

            return (
              <div
                key={key}
                className={cn(
                  'flex items-center gap-3 rounded-lg p-3 transition-colors',
                  meal ? 'cursor-pointer hover:bg-neutral-50' : 'opacity-50'
                )}
                onClick={() => meal && onMealClick?.(meal.id)}
              >
                <Icon className={cn('h-5 w-5 flex-shrink-0', color)} />
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-medium text-supporting">{label}</p>
                  <p className="truncate text-sm font-medium text-dusk">
                    {meal?.name || 'Not planned'}
                  </p>
                </div>
                {meal?.cookTime && (
                  <span className="text-xs text-supporting">
                    ⏱️ {meal.cookTime}m
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </DashboardCard>
  );
}
